/** A run's transcript: each turn, its trace, and what it produced. */
import {
  Fragment,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  useStatefulTurnTrace,
  type RunContextWindow,
  type DraftStatus,
  type RunDraft,
  type RunEmail,
  type RunMessage,
  type RunMessageDocument,
  type RunProjectView,
} from "../api/hooks/useStatefulAgents";
import { useRunOpened, useSetRunRead } from "../api/hooks/useUnreadAnswers";
import { UserContext } from "../auth";
import { formatTokens } from "../utils/formatTokens";
import { formatWhen } from "../utils/formatWhen";
import { reviewAttrs } from "../utils/reviewState";
import { toFirstUnread } from "../utils/scrollToLatest";
import {
  MANAGER_AGENT,
  agentDisplayLabel,
  agentsLabel,
  agentsWho,
  nameSeparator,
} from "../utils/runParticipants";
import { CollapsibleMarkdown } from "./CollapsibleMarkdown";
import { Markdown } from "./Markdown";
import { PeekButton } from "./PeekButton";
import { QueuedMessages } from "./QueuedMessages";
import { QueuedReviewComments, ReviewSubmission } from "./ReviewSubmission";
import { useReviewComments } from "../api/hooks/useReview";
import {
  type FeedbackQuestion,
  useFeedbackQuestions,
} from "../api/hooks/useAgentFeedback";
import { ConversationQuestion } from "./AgentFeedbackSection";
import { AnsweredQuestion } from "./AnsweredQuestion";
import { useUploadsDescribe } from "../api/hooks/useUploadsDescribe";
import { readFailureGroups } from "../utils/readFailures";
import { responseCounts } from "../utils/responseCounts";
import { EMAIL_MARKER } from "../utils/emailMarker";
import { turnTraceSteps } from "../utils/turnTraceLines";
import { ProjectResponse } from "./ProjectResponse";
import { ReviewProvider } from "./ReviewProvider";
import { AutomatedReviewReport } from "./AutomatedReviewReport";
import { SourceRefreshReport } from "./SourceRefreshReport";
import { EmailCard, type OnReply } from "./RunItemCards";
import { useCitations } from "./useCitations";
import { MessageImages } from "./MessageImages";
import { ContextBreakdown } from "./ContextBreakdown";
import { useBreakdownToggle } from "./useBreakdownToggle";
import styles from "./RunView.module.css";
import { Chevron } from "./Chevron";

// Must cover every value the BE writes to `incomplete_reason`, or the operator
// reads the raw token. `lost` is the reaper's.
const INCOMPLETE_LABEL: Record<string, string> = {
  stopped: "Stopped by an operator",
  turn_budget_exhausted: "Wrapped up early - ran out of tool turns",
  context_budget_exhausted: "Wrapped up early - context was full",
  failed: "This turn failed",
  lost: "This turn was lost - its worker stopped without finishing",
};

// Where each agent's context window finished, and what each compaction threw away.
// Collapsed by default: on a healthy turn it is a footnote, and the summary line
// carries the one number worth seeing at a glance.
const ContextWindowReport = ({ rows }: { rows: RunContextWindow[] }) => {
  const [open, setOpen] = useState(false);
  if (rows.length === 0) {
    return null;
  }
  const compactions = rows.reduce((n, r) => n + r.compactions.length, 0);
  const biggest = rows.reduce((a, b) => (b.peak > a.peak ? b : a));
  const ended = rows.reduce((n, r) => Math.max(n, r.used), 0);
  // The peak is the number the live gauge throws away, so it belongs on the collapsed
  // line whenever it differs - showing only where the window ENDED would hide exactly
  // what this card exists to surface.
  const summary =
    biggest.peak > ended
      ? `Context window ${formatTokens(ended)}, peaked at ${formatTokens(biggest.peak)}`
      : `Context window ${formatTokens(ended)}`;
  const across = rows.length === 1 ? "" : ` across ${rows.length} agents`;
  return (
    <div className={styles.turnContextReport}>
      <button
        type="button"
        className={styles.turnContextToggle}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <Chevron tree open={open} /> {summary}
        {across}
        {compactions > 0
          ? `, ${compactions} compaction${compactions === 1 ? "" : "s"}`
          : ""}
      </button>
      {open && (
        <ul className={styles.turnContextRows}>
          {rows.map((row) => (
            <ContextReportRow key={row.agent} row={row} />
          ))}
        </ul>
      )}
    </div>
  );
};

const ContextReportRow = ({ row }: { row: RunContextWindow }) => {
  const { open, button } = useBreakdownToggle();
  return (
            <li>
              <strong>{row.agent}</strong> ended at {formatTokens(row.used)}
              {row.window > 0 ? ` of ${formatTokens(row.window)}` : ""}
              {/* Only when it differs: on a turn that never compacted the peak IS
                  where it ended, and saying so twice reads as two facts. */}
              {row.peak > row.used
                ? `, peaked at ${formatTokens(row.peak)}`
                : ""}
              {row.composition && <> {button}</>}
              {open && row.composition && (
                <ContextBreakdown used={row.used} composition={row.composition} />
              )}
              {row.compactions.length > 0 && (
                <ul className={styles.turnContextCompactions}>
                  {row.compactions.map((c, i) => (
                    <li key={`${c.before}-${c.after}-${i}`}>
                      compaction {i + 1}: {formatTokens(c.before)} &rarr;{" "}
                      {formatTokens(c.after)} (freed{" "}
                      {formatTokens(Math.max(c.before - c.after, 0))})
                    </li>
                  ))}
                </ul>
              )}
            </li>
  );
};

// On the turn, not just the trace: a degraded turn still answers confidently
// (principle 21).
const TurnFlags = ({ message }: { message: RunMessage }) => {
  const flags = message.turn_flags ?? {};
  const notes: string[] = [];
  if (flags.incomplete_reason) {
    notes.push(
      INCOMPLETE_LABEL[flags.incomplete_reason] ?? flags.incomplete_reason,
    );
  }
  if (flags.compactions && !flags.context?.length) {
    // Only without the per-agent report, which says this with the numbers attached.
    notes.push(
      `Compacted its working context ${flags.compactions} time${flags.compactions === 1 ? "" : "s"}`,
    );
  }
  if (flags.emails_not_checked) {
    notes.push(
      "Could not check which emails have gone out, so an email listed may already be sent",
    );
  }
  if (flags.unreadable_images?.length) {
    notes.push(
      `Could not open ${flags.unreadable_images.join(", ")}, so the agent did not see ${
        flags.unreadable_images.length === 1 ? "it" : "them"
      }`,
    );
  }
  if (flags.emails_not_recorded) {
    notes.push(
      "This turn's emails were not saved for later runs, which will not know of them",
    );
  }
  const gate = flags.extraction_gate_degraded;
  const gatePending = gate?.pending ?? [];
  // Only after the agent was asked to conclude with the tool and still did not.
  if (flags.concluded_via === "prose") {
    notes.push("Ended without calling conclude_turn, even when asked");
  }
  if (flags.rules_applied?.length) {
    // This turn CHANGED the agent's future behaviour; the reply explains why.
    notes.push(
      `Changed its standing instructions: ${flags.rules_applied.join(", ")}`,
    );
  }
  if (flags.rules_failed?.length) {
    // Loud: the reply may claim a rule changed when it did not.
    notes.push(
      `COULD NOT change: ${flags.rules_failed.join(", ")} - the reply may claim otherwise`,
    );
  }
  const reads = readFailureGroups(flags.tool_failures ?? []);
  // Named, not coded: the operator has to know WHICH document each reason is about.
  const { byId } = useUploadsDescribe([
    ...(flags.tool_failures ?? []).flatMap((f) =>
      f.upload_id ? [f.upload_id] : [],
    ),
    ...gatePending,
  ]);
  if (gate) {
    const names = gatePending
      .map((id) => byId.get(id)?.display_name ?? id)
      .join(", ");
    const it = gatePending.length === 1 ? "it" : "them";
    notes.push(
      !gatePending.length
        ? gate.reason === "error"
          ? "Could not check that every document was ready when this turn ran"
          : "Some documents were still being prepared when this turn ran"
        : gate.reason === "released"
          ? `An operator continued without waiting for ${names}, so the agent did not read ${it}`
          : `Stopped waiting for ${names} at the time limit, so the agent did not read ${it}`,
    );
  }
  if (notes.length === 0 && reads.length === 0) {
    return null;
  }
  return (
    <ul className={styles.flags}>
      {reads.map((group) => (
        <li key={group.label}>
          {group.details.length ? (
            <details>
              <summary>{group.label}</summary>
              <ul>
                {group.details.map((d) => (
                  <li key={`${d.uploadId}|${d.detail}`}>
                    {d.uploadId && (
                      <strong>
                        {byId.get(d.uploadId)?.display_name ?? d.uploadId}:{" "}
                      </strong>
                    )}
                    {d.detail}
                  </li>
                ))}
              </ul>
            </details>
          ) : (
            group.label
          )}
        </li>
      ))}
      {notes.map((n) => (
        <li key={n}>{n}</li>
      ))}
    </ul>
  );
};

// A finished turn's trace, from the persisted S3 trace (the operation row is
// transient). Fetched only when opened, so a long conversation doesn't pull every
// trace.
const PersistedTurnTrace = ({
  runId,
  messageId,
  agent,
  participants,
  hasTrace,
}: {
  runId: string;
  messageId: string;
  agent?: string | null;
  participants?: readonly string[];
  // Whether a trace was CAPTURED; disambiguates an empty trace.
  hasTrace?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const { data, isLoading, isError } = useStatefulTurnTrace(
    runId,
    messageId,
    open,
  );
  const steps = turnTraceSteps(data ?? "", agentsLabel(agent));
  const settled = open && !isLoading && !isError;
  return (
    // Styled as the context-window line beside it: a footnote on a finished turn,
    // where the live feed is the thing an operator watches.
    <div className={styles.turnContextReport}>
      <button
        type="button"
        className={styles.turnContextToggle}
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <Chevron tree open={open} /> Actions taken during the turn by{" "}
        {agentsLabel(agent, participants)}
        {/* Unknown until the lazy fetch lands, so no number rather than a guess. */}
        {settled ? ` (${steps.length})` : ""}
      </button>
      {open && (
        <ul className={styles.eventFeedStatic}>
          {isLoading && <li className={styles.eventThinking}>Loading…</li>}
          {isError && (
            <li className={styles.eventThinking}>
              The trace for this turn could not be loaded.
            </li>
          )}
          {settled && steps.length === 0 && (
            <li className={styles.eventThinking}>
              {/* Answering without tools is ordinary, not a gap in the record,
                  and nothing here knows when a trace-less run happened. */}
              {hasTrace === false
                ? "No actions recorded for this step."
                : "No actions - the agent answered directly, without calling any tools."}
            </li>
          )}
          {steps.map((step, i) => (
            <li
              key={i}
              className={
                step.thinking ? styles.eventThinking : styles.eventTool
              }
            >
              <span className={styles.eventAgent}>{`[${step.who}] `}</span>
              {step.thinking ? step.text : `→ ${step.text}`}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// Who answered, each specialist's name opening where it stands on the project. The
// Manager keeps no status of its own, so its name stays plain, as does every name on a
// turn with no project.
const TurnAgents = ({
  agent,
  participants,
  projectId,
}: {
  agent: string | null | undefined;
  participants: string[] | undefined;
  projectId: string | null;
}) => {
  const who = agentsWho(agent, participants);
  if (who.length === 0) return <>{agentsLabel(agent, participants)}</>;
  return (
    <>
      {who.map((a, i) => (
        <Fragment key={a}>
          {nameSeparator(i, who.length)}
          {projectId && a !== MANAGER_AGENT ? (
            <PeekButton projectId={projectId} agent={a} look="link">
              {agentDisplayLabel(a)}
            </PeekButton>
          ) : (
            agentDisplayLabel(a)
          )}
        </Fragment>
      ))}
    </>
  );
};

/** Whether nobody sent this operator-role message.

    A scheduled pass's per-project brief occupies the model's user turn, so its
    role is right, but attributing it to a person is not. New rows carry the
    flag; older ones are recognised by having no sender, which only an automated
    message can have. */
const isAutomated = (m: RunMessage): boolean =>
  m.role === "operator" && (!!m.turn_flags?.automated || !m.sender_email);

// A brief opens on its own title ("## <project>").
const firstLineTitle = (text: string): string =>
  (text.split("\n").find((line) => line.trim()) ?? "")
    .replace(/^#+\s*/, "")
    .replace(/^[_*]+|[_*]+$/g, "")
    .trim();

// A message the operator rarely needs to read, folded to one line.
const FoldedTurn = ({
  title,
  when,
  defaultOpen,
  children,
}: {
  title: string;
  when: string;
  defaultOpen: boolean;
  children: ReactNode;
}) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <>
      <button
        type="button"
        className={styles.changeHeader}
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <span className={styles.changeCaret} aria-hidden="true">
          <Chevron tree open={open} />
        </span>
        <span className={styles.changeTitle}>{title}</span>
        <span className={styles.muted}>{formatWhen(when)}</span>
      </button>
      {open && children}
    </>
  );
};

const placedPositions = (content: string): Set<number> =>
  new Set([...content.matchAll(EMAIL_MARKER)].map((match) => Number(match[1])));

/** A conversation's draft as the card a scheduled pass shows. */
const draftEmail = (
  draft: RunDraft,
  stored: DraftStatus | undefined,
): RunEmail => ({
  draft_id: draft.draft_id ?? "",
  author: draft.author ?? "",
  author_label: draft.author ? agentDisplayLabel(draft.author) : "",
  to: draft.to,
  subject: draft.subject,
  body: draft.body,
  trigger: draft.trigger,
  why: draft.why ?? "",
  depends_on: draft.depends_on ?? [],
  merges: draft.merges ?? [],
  thread: draft.thread ?? "",
  discharges: [],
  previous_draft_id: null,
  first_drafted_at: null,
  carried: false,
  shown_later: false,
  status: stored?.status ?? "current",
  sent_at: stored?.sent_at ?? null,
});

const TurnDrafts = ({
  drafts,
  draftStatus,
  onReply,
  projectName,
  openEmails,
}: {
  drafts: readonly RunDraft[];
  draftStatus: Readonly<Record<string, DraftStatus>>;
  onReply: OnReply;
  projectName: string;
  openEmails: boolean;
}) => (
  <section className={styles.turnDrafts}>
    <h4 className={styles.changeGroup}>Ready to send ({drafts.length})</h4>
    {drafts.map((draft, i) => (
      <EmailCard
        key={`${i}-${draft.to}`}
        email={draftEmail(draft, draftStatus[draft.draft_id ?? ""])}
        onReply={onReply}
        inline={false}
        projectName={projectName}
        defaultOpen={openEmails}
      />
    ))}
  </section>
);

/** A scheduled pass's closing list of what waited on the operator, recorded before
 * prompt changes stopped waiting on anyone. Its decisions sit on their project. */
const isClosingList = (m: RunMessage): boolean =>
  !!(m.turn_flags?.prompt_changes || m.turn_flags?.needs_you);

// Mark as unread, offered once an agent has replied to something the operator sent.
const MarkUnread = ({ runId, email }: { runId: string; email: string }) => {
  const setRead = useSetRunRead(email);
  return (
    <p className={styles.readControls}>
      <button
        type="button"
        className={styles.readButton}
        disabled={setRead.isLoading || setRead.isSuccess}
        onClick={() => setRead.mutate({ runId, read: false })}
      >
        Mark as unread
      </button>
      {setRead.isSuccess && (
        <span className={styles.muted}>
          Back in My actions, under Unread answers.
        </span>
      )}
      {setRead.error && (
        <span className={styles.error}>
          Could not mark it unread ({setRead.error.message}).
        </span>
      )}
    </p>
  );
};

/** A message's attached documents as citations, so each opens like any cited source;
 * one the turn has not yet filed (or could not open) by its name. */
const attachedDocumentsBody = (documents: RunMessageDocument[] | undefined): string =>
  documents?.length
    ? `Attached: ${documents
        .map((d) => (d.upload_id ? `[${d.upload_id}]` : d.name))
        .join(", ")}`
    : "";

/** The conversation itself: each turn, its trace, and what it produced. */
export const RunTranscript = ({
  runId,
  runKind,
  projectId,
  seen,
  messages,
  projects,
  draftStatus,
  onReply,
  openResponses,
  openEmails,
}: {
  runId: string;
  /** The run's kind: an open of a scheduled run also starts its sources' refresh. */
  runKind: string;
  /** The project the conversation is about (the run's ``context_project_id``): text
   * selected in a reply is a comment on it. Null for a conversation about no single
   * project, where a reply not tied to a project is quoted into the reply box instead. */
  projectId: string | null;
  /** Whether the transcript is in front of the operator, so showing it marks its
   * replies read. False while it is mounted but hidden. */
  seen: boolean;
  messages: readonly RunMessage[];
  /** Whether each email on the messages has since gone out or been replaced. */
  draftStatus: Readonly<Record<string, DraftStatus>>;
  /** Each project a scheduled pass worked, keyed by its report message; [] elsewhere. */
  projects: readonly RunProjectView[];
  /** Quotes a card into the composer; null where there is no composer. */
  onReply: OnReply;
  /** A run cut down to one project opens that project's response. */
  openResponses: boolean;
  /** Shows each email itself rather than folding it to its subject line. */
  openEmails: boolean;
}) => {
  // A review started before its first message carried its comment ids: the comments
  // this conversation was started with stand in for the flag.
  const reviewComments = useReviewComments({ run_id: runId }).data?.comments;
  const firstOperator = messages.find((m) => m.role === "operator");
  // The questions this conversation asked, each under the reply whose turn asked it: the
  // first agent message written after it. One asked by a turn still running waits below.
  const asksQuestions = runKind === "chat" || runKind === "agent_chat";
  const askedQuery = useFeedbackQuestions(
    asksQuestions ? { run_id: runId } : null,
  );
  const lastMessageId = messages.length
    ? messages[messages.length - 1].message_id
    : null;
  const refetchAsked = askedQuery.refetch;
  // refetch() ignores `enabled`: unguarded, a scheduled run sends a scopeless request
  // the route refuses.
  useEffect(() => {
    if (asksQuestions && lastMessageId) void refetchAsked();
  }, [asksQuestions, lastMessageId, refetchAsked]);
  const { askedUnder, askedLater } = useMemo(() => {
    const under = new Map<string, FeedbackQuestion[]>();
    const later: FeedbackQuestion[] = [];
    const questions = askedQuery.data?.questions ?? [];
    // A reworded question stands in for the one it replaced, under the turn that
    // reworded it: it keeps the original's asked date, so place it by its own asking.
    const reworded = new Set(questions.map((q) => q.replaces_question_id));
    for (const q of questions) {
      if (reworded.has(q.question_id)) continue;
      const asked =
        q.events.find((e) => e.kind === "asked")?.at ?? q.created_at;
      const reply = messages.find(
        (m) =>
          m.role === "agent" &&
          Date.parse(m.created_at) >= Date.parse(asked),
      );
      if (reply)
        under.set(reply.message_id, [
          ...(under.get(reply.message_id) ?? []),
          q,
        ]);
      else later.push(q);
    }
    return { askedUnder: under, askedLater: later };
  }, [askedQuery.data, messages]);
  const legacyReviewIds = useMemo(
    () =>
      firstOperator && !firstOperator.turn_flags?.review_comments
        ? (reviewComments ?? [])
            .filter(
              (c) =>
                c.conversation_id === runId &&
                // Not the follow-ups agreed later in the conversation.
                Date.parse(c.created_at) <=
                  Date.parse(firstOperator.created_at) + 60_000,
            )
            .map((c) => c.comment_id)
        : [],
    [firstOperator, reviewComments, runId],
  );
  // Every turn's prose at once, so the cited documents are named in one request
  // rather than one per message.
  const bodies = useMemo(
    () => [
      ...messages.map((m) => m.content),
      // Attached documents resolve with the rest, so their chips name them.
      ...messages.map((m) => attachedDocumentsBody(m.documents)),
      ...projects.flatMap((p) =>
        [...p.specialists, ...p.earlier].map((s) => s.reply),
      ),
    ],
    [messages, projects],
  );
  const byReport = new Map(projects.map((p) => [p.report_message_id, p]));
  // Each project's latest write-up in this conversation, from the messages themselves:
  // a page that carries no project views must not call the latest one replaced.
  const latestWriteUp = new Map<string, string>();
  for (const m of messages) {
    if (m.role === "agent" && m.project_id)
      latestWriteUp.set(m.project_id, m.message_id);
  }
  const projectNameOf = (projectId: string | null | undefined): string =>
    projects.find((p) => p.project_id === projectId)?.project_name ?? "";
  const { transform, components, modal } = useCitations(bodies);
  const { user } = useContext(UserContext);
  const email = user?.email ?? "";
  const { firstUnread, error: openedError } = useRunOpened({
    runId,
    throughSeq: messages.length ? messages[messages.length - 1].seq : null,
    seen,
    email,
  });
  const divider = useRef<HTMLLIElement>(null);
  // Once per open: a reply landing while the operator reads moves the divider, not them.
  const scrolledFor = useRef<string | null>(null);
  useEffect(() => {
    if (!firstUnread || scrolledFor.current === runId) return;
    scrolledFor.current = runId;
    if (divider.current) toFirstUnread(divider.current);
  }, [firstUnread, runId]);
  // A reply after the operator's latest message: what Mark as unread puts back. A turn
  // carrying only pushes and resolves is not theirs. Must agree with run_reads._mine.
  const changeIds = new Set(
    (reviewComments ?? [])
      .filter((c) => c.anchor.level === "operator_change")
      .map((c) => c.comment_id),
  );
  const changesOnly = (m: RunMessage) => {
    const ids = m.turn_flags?.review_comments ?? [];
    return ids.length > 0 && ids.every((id) => changeIds.has(id));
  };
  const lastMine = messages.reduce(
    (at, m, i) =>
      m.role === "operator" &&
      !!email &&
      m.sender_email === email &&
      !changesOnly(m)
        ? i
        : at,
    -1,
  );
  const answered =
    lastMine >= 0 && messages.slice(lastMine).some((m) => m.role === "agent");
  return (
    <>
      {openedError && (
        <p className={styles.error}>
          {runKind === "scheduled"
            ? `Could not start refreshing Gmail and Drive (${openedError.message}). It will run when you send a reply.`
            : `Could not record that you opened this conversation (${openedError.message}), so it may still show as unread.`}
        </p>
      )}
      {/* Text selected in an agent's reply is a comment on it, for that agent, on the
          reply's own project (a scheduled pass spans several) or else the
          conversation's. */}
      <ReviewProvider
        available
        defaultDoc={projectId ? `project:${projectId}` : null}
        defaultLevel="conversation_reply"
        comments={[]}
        projectId={projectId}
        onQuote={onReply}
      >
        <ol className={styles.transcript}>
          {messages.map((m, i) => {
            const automated = isAutomated(m);
            if (isClosingList(m)) return null;
            const view = byReport.get(m.message_id);
            const previous = i > 0 ? messages[i - 1] : null;
            const responseTo =
              m.role === "agent" && previous && isAutomated(previous)
                ? previous
                : null;
            // How the turn went, under the answer: flags, its activity log, then the
            // context windows it ended on.
            const footer = (
              <>
                <TurnFlags message={m} />
                {m.turn_flags?.source_refresh && (
                  <SourceRefreshReport refresh={m.turn_flags.source_refresh} />
                )}
                {m.turn_flags?.automated_review && m.project_id && (
                  <AutomatedReviewReport
                    review={m.turn_flags.automated_review}
                    projectId={m.project_id}
                  />
                )}
                {m.role === "agent" && (
                  <PersistedTurnTrace
                    runId={runId}
                    messageId={m.message_id}
                    agent={m.agent}
                    participants={m.participants}
                    hasTrace={m.has_ai_trace}
                  />
                )}
                {m.turn_flags?.context?.length ? (
                  <ContextWindowReport rows={m.turn_flags.context} />
                ) : null}
              </>
            );
            const placed = placedPositions(m.content);
            const unplaced = m.drafts.filter((_, n) => !placed.has(n + 1));
            const turnBody = (
              <>
                <div className={styles.turnHead}>
                  {m.role === "operator" ? (
                    m.sender_email
                  ) : (
                    <TurnAgents
                      agent={m.agent}
                      participants={m.participants}
                      projectId={m.project_id ?? projectId}
                    />
                  )}
                  <span className={styles.muted}>
                    {" "}
                    · {formatWhen(m.created_at)}
                  </span>
                </div>
                {m.role === "agent" ? (
                  <CollapsibleMarkdown
                    components={components}
                    transform={transform}
                    email={(marker) => {
                      const draft = m.drafts[Number(marker) - 1];
                      return draft ? (
                        <EmailCard
                          email={draftEmail(
                            draft,
                            draftStatus[draft.draft_id ?? ""],
                          )}
                          onReply={onReply}
                          inline={false}
                          projectName={projectNameOf(m.project_id)}
                          defaultOpen={openEmails}
                        />
                      ) : null;
                    }}
                  >
                    {m.content}
                  </CollapsibleMarkdown>
                ) : m.turn_flags?.answered_question ? (
                  <AnsweredQuestion answer={m.turn_flags.answered_question} />
                ) : m.turn_flags?.review_comments?.length ||
                  (m === firstOperator && legacyReviewIds.length) ? (
                  <ReviewSubmission
                    runId={runId}
                    projectId={m.project_id ?? null}
                    commentIds={m.turn_flags?.review_comments ?? legacyReviewIds}
                  />
                ) : (
                  <div className={styles.turnBody}>{m.content}</div>
                )}
                {m.images?.length ? <MessageImages images={m.images} /> : null}
                {m.documents?.length ? (
                  <div className={styles.attachedDocuments}>
                    <Markdown components={components}>
                      {transform(attachedDocumentsBody(m.documents))}
                    </Markdown>
                  </div>
                ) : null}
                {/* Only what the answer did not place: turns from before placement. */}
                {/* A write-up's emails show only inside its specialists' sections. */}
                {unplaced.length > 0 && !m.project_id && (
                  <TurnDrafts
                    drafts={unplaced}
                    draftStatus={draftStatus}
                    onReply={onReply}
                    projectName={projectNameOf(m.project_id)}
                    openEmails={openEmails}
                  />
                )}
                {askedUnder.get(m.message_id)?.map((q) => (
                  <ConversationQuestion
                    key={q.question_id}
                    question={q}
                    runId={runId}
                  />
                ))}
                {footer}
              </>
            );
            const folded = automated;
            return (
              <Fragment key={m.message_id}>
                {m.message_id === firstUnread && (
                  <li ref={divider} className={styles.newDivider} data-first-unread>
                    New
                  </li>
                )}
                <li
                  {...(m.role === "agent"
                    ? reviewAttrs(
                        "conversation_reply",
                        `${agentDisplayLabel(m.agent || "manager")}'s reply`,
                        {
                          agent: m.agent ?? "",
                          itemId: m.message_id,
                          doc: m.project_id ? `project:${m.project_id}` : undefined,
                        },
                      )
                    : {})}
                  className={[
                    m.role === "operator"
                      ? styles.operatorTurn
                      : styles.agentTurn,
                    folded ? styles.foldedTurn : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  {automated ? (
                    <FoldedTurn
                      title={`Brief: ${firstLineTitle(m.content)}`}
                      when={m.created_at}
                      defaultOpen={false}
                    >
                      <CollapsibleMarkdown
                        components={components}
                        transform={transform}
                        email={() => null}
                      >
                        {m.content}
                      </CollapsibleMarkdown>
                    </FoldedTurn>
                  ) : responseTo && view ? (
                    <FoldedTurn
                      title={`Response: ${view.project_name} · ${responseCounts(view)}`}
                      when={m.created_at}
                      defaultOpen={openResponses}
                    >
                      <ProjectResponse
                        runId={runId}
                        project={view}
                        onReply={onReply}
                        citations={{ components, transform }}
                      />
                      {footer}
                    </FoldedTurn>
                  ) : view ? (
                    // A conversation turn that changed the project: its answer, then the
                    // project's Next actions it rebuilt, every email and decision in the
                    // section that explains it.
                    <>
                      {turnBody}
                      <FoldedTurn
                        title={`Briefing updated · ${responseCounts(view)}`}
                        when={m.created_at}
                        defaultOpen={false}
                      >
                        <ProjectResponse
                          runId={runId}
                          project={view}
                          onReply={onReply}
                          citations={{ components, transform }}
                        />
                      </FoldedTurn>
                    </>
                  ) : m.role === "agent" &&
                    m.project_id &&
                    !responseTo &&
                    latestWriteUp.get(m.project_id) !== m.message_id ? (
                    // An earlier write-up in this conversation: a later one is the project's
                    // briefing now.
                    <>
                      {turnBody}
                      <p className={`${styles.muted} ${styles.replacedNote}`}>
                        {"\u2139\ufe0f"} Briefing updated (since replaced by a
                        later update)
                      </p>
                    </>
                  ) : responseTo ? (
                    // A scheduled pass's per-site report, folded like its brief: the
                    // operator opens the sites they care about.
                    <FoldedTurn
                      title={`Response: ${firstLineTitle(responseTo.content)}${
                        m.drafts.length
                          ? ` · ${m.drafts.length} email${m.drafts.length === 1 ? "" : "s"}`
                          : ""
                      }`}
                      when={m.created_at}
                      defaultOpen={openResponses}
                    >
                      {turnBody}
                    </FoldedTurn>
                  ) : (
                    turnBody
                  )}
                </li>
              </Fragment>
            );
          })}
        </ol>
      </ReviewProvider>
      {askedQuery.isError && (
        <p className={styles.error}>
          Could not load the questions asked in this conversation.
        </p>
      )}
      {askedLater.map((q) => (
        <ConversationQuestion key={q.question_id} question={q} runId={runId} />
      ))}
      {answered && <MarkUnread runId={runId} email={email} />}
      <QueuedMessages runId={runId} />
      <QueuedReviewComments
        runId={runId}
        projectId={firstOperator?.project_id ?? null}
      />
      {modal}
    </>
  );
};
